<script lang="ts">
  // The night's feed: live analysis (written by Claude from the published data, checked against it before posting)
  // and results-desk notes. Home shows everything; a race page passes raceId and shows only posts tagged with it.
  // In a replay or when scrubbing back, only posts written up to the moment being viewed are shown.
  import type { NoteItem, Notes, Overview } from '../../types';
  import { app } from '../../lib/app.svelte';
  import { fmtStamp } from '../../lib/format';
  import { counterpartId } from '../../lib/race';
  let { notes, ov, raceId = null, first = 4, title = 'Live analysis' }:
    { notes: Notes | null; ov: Overview | null; raceId?: string | null; first?: number; title?: string } = $props();

  let open = $state(false);
  // unique: a repeated id is a duplicate {#each} key, which throws and blanks the page
  const tags = (n: NoteItem) => [...new Set(n.race_ids ?? (n.race_id ? [n.race_id] : []))];
  // only posts up to the snapshot on screen (scrubbing back, a replay, or while the newest one is still loading)
  const cut = $derived(app.isLive && app.shownSid === app.liveSid ? null : app.shownTime);
  // a race page also shows posts about the same contest in its other view (Charleston County part ↔ full district)
  const mine = $derived(raceId ? new Set([raceId, counterpartId(raceId) ?? raceId]) : null);
  const items = $derived(
    [...(notes?.items ?? [])]
      .filter((n) => cut === null || Date.parse(n.ts) <= cut)
      .filter((n) => !mine || tags(n).some((t) => mine.has(t)))
      .sort((a, b) => Date.parse(b.ts) - Date.parse(a.ts)),
  );
  const shown = $derived(open ? items.slice(0, 40) : items.slice(0, first));
  const races = $derived(new Map((ov?.races ?? []).map((r) => [r.race_id, r])));
  const label = (id: string) => {
    const r = races.get(id);
    return r ? (r.subtitle ? `${r.title} · ${r.subtitle}` : r.title) : null;
  };
  const hasAnalysis = $derived(items.some((n) => n.kind === 'analysis'));
</script>

{#if !raceId || items.length}
  <section class="card notes" aria-label={title}>
    <div class="head">
      <div class="kicker">{title}</div>
      {#if items.length}<span class="upd">Updated {fmtStamp(items[0]!.ts, app.now)}</span>{/if}
    </div>
    {#if items.length === 0}
      <p class="muted empty">{app.preClose ? 'Analysis of the results starts when polls close at 7 PM.' : 'No updates yet tonight.'}</p>
    {:else}
      <ol>
        {#each shown as n, i (`${n.id ?? n.ts + n.text}#${i}`)}
          <li class:desk={n.kind !== 'analysis'}>
            <div class="meta">
              <time datetime={n.ts}>{fmtStamp(n.ts, app.now)}</time>
              <span class="by">{n.kind === 'analysis' ? 'Analysis' : 'Results desk'}</span>
              {#if n.kind === 'analysis'}<span class="ai" title="Written by Claude (AI) from the official counts and our estimates">AI</span>{/if}
            </div>
            {#if n.headline}<h3>{n.headline}</h3>{/if}
            <p>{n.text}</p>
            {#if tags(n).some((t) => !mine?.has(t) && label(t))}
              <div class="chips">
                {#each tags(n).filter((t) => !mine?.has(t) && label(t)) as t (t)}
                  <a class="chip" href={app.href(`#/race/${encodeURIComponent(t)}`)}>{label(t)} <span aria-hidden="true">→</span></a>
                {/each}
              </div>
            {/if}
          </li>
        {/each}
      </ol>
      {#if items.length > first}
        <button class="more" type="button" aria-expanded={open} onclick={() => (open = !open)}>
          {open ? 'Show fewer' : `Show ${Math.min(items.length, 40) - first} more`}
        </button>
      {/if}
      {#if hasAnalysis}
        <p class="foot">Analysis posts are written by Claude, an AI model, from the official counts and this site’s estimates, and checked against that data before they’re published.</p>
      {/if}
    {/if}
  </section>
{/if}

<style>
  .notes { display: flex; flex-direction: column; gap: 12px; }
  .head { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; }
  .upd { font-size: 0.74rem; color: var(--ink-3); }
  ol { list-style: none; margin: 0; padding: 0; }
  li { position: relative; padding: 0 0 18px 20px; }
  li::before { content: ''; position: absolute; left: 4px; top: 5px; width: 8px; height: 8px; border-radius: 50%; background: var(--est); }
  li.desk::before { background: var(--ink-3); }
  li::after { content: ''; position: absolute; left: 7.5px; top: 17px; bottom: 2px; width: 1px; background: var(--line-2); }
  li:last-child::after { display: none; }
  .meta { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; font: 700 0.74rem/1.2 var(--font); color: var(--ink-3); }
  .by { font-weight: 600; }
  .ai { font-size: 0.64rem; letter-spacing: 0.04em; padding: 1px 5px; border-radius: 5px; color: var(--est-ink); background: var(--est-bg);
    border: 1px solid var(--est-line); }
  h3 { margin: 5px 0 0; font: 700 0.98rem/1.3 var(--display, var(--font)); }
  p { margin: 4px 0 0; font-size: 0.9rem; line-height: 1.5; }
  .chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
  .chip { font-size: 0.76rem; font-weight: 600; padding: 3px 9px; border-radius: 999px; background: var(--card-3);
    color: var(--ink); text-decoration: none; border: 1px solid var(--line); }
  .chip:hover { border-color: var(--ink-3); }
  .more { align-self: flex-start; border: 1px solid var(--line); background: var(--card); color: var(--ink); font: 600 0.85rem/1 var(--font);
    padding: 9px 14px; border-radius: 999px; cursor: pointer; }
  .foot { margin: 2px 0 0; font-size: 0.74rem; line-height: 1.4; color: var(--ink-3); }
  .empty { margin: 0; font-size: 0.9rem; }
</style>
